import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import api from '../config/api';
import logo from '../assets/images/logo.svg';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data.data;

      if (!user || user.role !== 'ADMIN') {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      onLogin(token);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* Left Branding Panel */}
      <Box
        sx={{
          flex: isMobile ? 'none' : 1,
          background: 'linear-gradient(135deg, #005A64 0%, #32A6D8 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 4 : 6,
          minHeight: isMobile ? 180 : 'auto',
          color: '#fff',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="OkTreat Logo"
          sx={{
            width: isMobile ? 80 : 120,
            height: 'auto',
            mb: 3,
            filter: 'brightness(0) invert(1)',
          }}
        />
        <Typography
          variant={isMobile ? 'h5' : 'h3'}
          fontWeight={700}
          sx={{ fontFamily: "'Poppins', sans-serif", textAlign: 'center' }}
        >
          OkTreat
        </Typography>
        <Typography
          variant={isMobile ? 'body2' : 'h6'}
          sx={{
            mt: 1,
            opacity: 0.85,
            fontFamily: "'Poppins', sans-serif",
            textAlign: 'center',
            maxWidth: 360,
          }}
        >
          Admin Dashboard
        </Typography>
        {!isMobile && (
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              opacity: 0.7,
              fontFamily: "'Poppins', sans-serif",
              textAlign: 'center',
              maxWidth: 320,
            }}
          >
            Manage sitters, bookings, reviews, and everything in between.
          </Typography>
        )}
      </Box>

      {/* Right Login Form Panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 3 : 6,
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Logo above the form on mobile (since brand panel is smaller) */}
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={logo}
                alt="OkTreat Logo"
                sx={{ width: 48, height: 'auto' }}
              />
            </Box>
          )}

          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{ fontFamily: "'Poppins', sans-serif", color: '#005A64' }}
          >
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Sign in to access the admin dashboard
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Email Address"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                },
              }}
            />
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                background: 'linear-gradient(135deg, #005A64 0%, #32A6D8 100%)',
                boxShadow: '0 4px 14px rgba(0, 90, 100, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #004850 0%, #2890BC 100%)',
                  boxShadow: '0 6px 20px rgba(0, 90, 100, 0.4)',
                },
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center', mt: 4 }}
          >
            OkTreat Admin Panel
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
